import { FocusEvent, KeyboardEvent, MouseEvent, ReactNode, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DROPDOWN_V2_CLEAR_VALUE, DropdownV2Props } from "../Dropdown/DropdownV2";
import { OptionV2 } from "../types";
import { formatErrorMessageV2 } from "../utils";
import { useTranslation } from "react-i18next";

export type MultiDropdownV2Props<T = any> = Omit<DropdownV2Props<T>, "value" | "onChange"> & {
  value?: OptionV2<T>[];
  onChange?: (options: OptionV2<T>[], action?: "selectAll") => void;
  getOptionLabel?: (option: OptionV2<T>) => string;
  renderOption?: (option: OptionV2<T>) => ReactNode;
  onOptionFocus?: (option: OptionV2<T>) => void;
  selectAll?: boolean;
  selectAllLabel?: string;
  printEmptyLabel?: string;
};

const SELECT_ALL_VALUE = "__SELECT_ALL__";
const defaultOptionLabel = (option: OptionV2) => option.label;

export function MultiDropdownV2<T = any>({
  id,
  name,
  label,
  description = "",
  value = [],
  options = [],
  placeholder,
  disabled = false,
  searchable = false,
  error,
  emptyMessage,
  menuMount = "static",
  clearOptionLabel,
  onChange,
  onBlur,
  renderOption,
  getOptionLabel = defaultOptionLabel,
  onOptionFocus,
  selectAll = false,
  selectAllLabel,
  printEmptyLabel,
}: MultiDropdownV2Props<T>) {
  const { t, i18n } = useTranslation();
  const resolvedPlaceholder = placeholder || t("reports.select");
  const resolvedEmptyMessage = emptyMessage || t("commonUi.noOptions");
  const resolvedSelectAllLabel = selectAllLabel || t("excelExport.selectAll");
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [summarized, setSummarized] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const tags = tagsRef.current;
    if (!trigger || !tags) return;
    const measure = () => {
      // Print hides the interactive trigger; preserve its screen summary.
      if (window.matchMedia("print").matches || !trigger.getClientRects().length) return;
      const style = getComputedStyle(trigger);
      const inset = [style.paddingTop, style.paddingBottom, style.borderTopWidth, style.borderBottomWidth]
        .reduce((total, size) => total + (parseFloat(size) || 0), 0);
      setSummarized(value.length > 0 && tags.getBoundingClientRect().height + inset > 130);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(trigger);
    observer.observe(tags);
    return () => observer.disconnect();
  }, [value, disabled, getOptionLabel, i18n.language]);
  const selectId = id || `onboarding-v2-${name}`;

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return options;
    return options.filter((option) => getOptionLabel(option).toLowerCase().includes(normalizedSearch));
  }, [options, search, getOptionLabel, i18n.language]);

  const allSelected = useMemo(
    () =>
      options.length > 0 &&
      options.every((option) =>
        value.some((selected) => String(selected.value) === String(option.value)),
      ),
    [options, value],
  );

  const showSelectAll = selectAll && options.length > 1;

  const menuOptions = useMemo(() => {
    const base = showSelectAll
      ? [
        {
          label: resolvedSelectAllLabel,
          value: SELECT_ALL_VALUE as unknown as T,
          info: { isSelectAll: true },
        } as OptionV2<T>,
        ...filteredOptions,
      ]
      : filteredOptions;
    if (!clearOptionLabel) return base;
    const clearOption: OptionV2<T> = {
      label: clearOptionLabel,
      value: DROPDOWN_V2_CLEAR_VALUE as unknown as T,
      info: { __clearSelection: true },
    };
    return [clearOption, ...base];
  }, [showSelectAll, resolvedSelectAllLabel, filteredOptions, clearOptionLabel]);

  function isSelected(option: OptionV2<T>) {
    return value.some((selected) => String(selected.value) === String(option.value));
  }

  function isSelectAllOption(option: OptionV2<T>) {
    return String(option.value) === SELECT_ALL_VALUE;
  }

  function isClearOption(option: OptionV2<T>) {
    return Boolean(clearOptionLabel) && String(option.value) === DROPDOWN_V2_CLEAR_VALUE;
  }

  function handleToggle() {
    if (!disabled) setIsOpen((current) => !current);
  }

  function handleSelect(option: OptionV2<T>) {
    if (isClearOption(option)) {
      onChange && onChange([]);
      setSearch("");
      return;
    }
    if (isSelectAllOption(option)) {
      if (allSelected) onChange && onChange([]);
      else onChange && onChange(options, "selectAll");
      setSearch("");
      return;
    }
    const nextValue = isSelected(option)
      ? value.filter((selected) => String(selected.value) !== String(option.value))
      : [...value, option];
    onChange && onChange(nextValue);
  }

  function handleRemoveSelected(event: MouseEvent<HTMLButtonElement>, option: OptionV2<T>) {
    event.preventDefault();
    event.stopPropagation();
    onChange && onChange(value.filter((selected) => String(selected.value) !== String(option.value)));
  }

  function handleOptionMouseDown(event: MouseEvent<HTMLButtonElement>) {
    // Safari may report a null relatedTarget while moving focus to a menu option.
    // Keeping focus on the trigger prevents the wrapper blur from closing the
    // menu before the option's click event is handled.
    event.preventDefault();
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleToggle();
  }

  function handleTriggerMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (!disabled) return;
    event.preventDefault();
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setIsOpen(false);
    setSearch("");
    onBlur && onBlur();
  }

  const menuClassName =
    menuMount === "overlay"
      ? "ov2-dropdown__menu ov2-dropdown__menu--overlay"
      : "ov2-dropdown__menu";

  const trigger = (
    <div
      className={`ov2-dropdown__trigger ${isOpen ? "ov2-dropdown__trigger--open" : ""} ${error ? "ov2-input--error" : ""} ${disabled ? "ov2-input--disabled" : ""}`}
      ref={triggerRef}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-expanded={isOpen}
      onMouseDown={handleTriggerMouseDown}
      onClick={handleToggle}
      onKeyDown={handleTriggerKeyDown}
    >
      <span className="ov2-multidropdown__selection">
      {summarized && <span className="ov2-multidropdown__summary">{t("multiDropdown.selectedCount", { count: value.length })}</span>}
      <span ref={tagsRef} aria-hidden={summarized || undefined} style={summarized ? { position: "absolute", visibility: "hidden", width: "100%", top: 0, left: 0 } : undefined} className={value.length ? "ov2-dropdown__value ov2-multidropdown__trigger-value" : "ov2-dropdown__placeholder"}>
        {value.length
          ? value.map((option) => (
            <span className="ov2-multidropdown__tag" key={`${option.value}`}>
              <span>{getOptionLabel(option)}</span>
              {!disabled && (
                <button
                  className="ov2-multidropdown__tag-remove"
                  type="button"
                  aria-label={t("multiDropdown.remove", { name: getOptionLabel(option) })}
                  tabIndex={summarized ? -1 : undefined}
                  onClick={(event) => handleRemoveSelected(event, option)}
                >
                  ×
                </button>
              )}
            </span>
          ))
          : resolvedPlaceholder}
      </span>
      </span>
      {!disabled && (
        <span className={`ov2-dropdown__chevron ${isOpen ? "ov2-dropdown__chevron--open" : ""}`}>⌃</span>
      )}
    </div>
  );

  function renderMenu() {
    if (!isOpen) return null;
    return (
      <div className={menuClassName}>
        {searchable && (
          <input
            className="ov2-dropdown__search"
            value={search}
            placeholder={`${t("commonUi.search")}...`}
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
        )}
        <ul>
          {menuOptions.length > 0 ? (
            menuOptions.map((option) => {
              const selectAllRow = isSelectAllOption(option);
              const clearRow = isClearOption(option);
              const selected = selectAllRow ? allSelected : !clearRow && isSelected(option);
              return (
                <li key={`${option.value}`}>
                  <button
                    className={`ov2-multidropdown__option ${selected ? "ov2-multidropdown__option--selected" : ""}`}
                    type="button"
                    onMouseDown={handleOptionMouseDown}
                    onMouseEnter={() => !selectAllRow && !clearRow && onOptionFocus?.(option)}
                    onFocus={() => !selectAllRow && !clearRow && onOptionFocus?.(option)}
                    onClick={() => {
                      if (!selectAllRow && !clearRow) onOptionFocus?.(option);
                      handleSelect(option);
                    }}
                  >
                    <span className="ov2-multidropdown__checkbox">{selected ? "✓" : ""}</span>
                    {selectAllRow || clearRow ? <span>{option.label}</span> : renderOption ? renderOption(option) : <span>{getOptionLabel(option)}</span>}
                  </button>
                </li>
              );
            })
          ) : (
            <li className="ov2-dropdown__empty">{resolvedEmptyMessage}</li>
          )}
        </ul>
      </div>
    );
  }

  return (
    <div className="ov2-field ov2-dropdown ov2-multidropdown" id={selectId} onBlur={handleBlur}>
      {label && <span className="ov2-field__label">{label}</span>}
      <span className="report-print-value">{summarized
        ? t("multiDropdown.selectedCount", { count: value.length })
        : value.map(option => getOptionLabel(option)).join(", ") || printEmptyLabel || t("reportLayout.none")}</span>
      {description && <span className="ov2-field__description">{description}</span>}
      {menuMount === "overlay" ? (
        <div className="ov2-dropdown__anchor">
          {trigger}
          {renderMenu()}
        </div>
      ) : (
        <>
          {trigger}
          {renderMenu()}
        </>
      )}
      {error && <small className="ov2-field__error animation__fadeIn">{formatErrorMessageV2(error)}</small>}
    </div>
  );
}
